import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import { _nextTrackEvent } from '../index';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

const EVENT_IDS = {
  START_ONBOARDING: '5998',
  PROFILE_BUILDER_NEXT: '3553',
  PROFILE_BUILDER_SAVE_AND_EXIT: '3554',
  ADDRESS_ENTRY_NEXT: '3555',
  ADDRESS_ENTRY_GO_BACK: '3556',
  ADDRESS_ENTRY_EXIT: '3557',
  ADDRESS_VERIFICATION_GO_BACK: '3558',
  ADDRESS_VERIFICATION_EXIT: '3559',
  CREATE_LISTING: '3560',
} as const;

// Export event IDs for data-eventid attributes
export const START_ONBOARDING_EVENT_ID = EVENT_IDS.START_ONBOARDING;
export const PROFILE_BUILDER_NEXT_EVENT_ID = EVENT_IDS.PROFILE_BUILDER_NEXT;
export const PROFILE_BUILDER_SAVE_AND_EXIT_EVENT_ID = EVENT_IDS.PROFILE_BUILDER_SAVE_AND_EXIT;
export const ADDRESS_ENTRY_NEXT_EVENT_ID = EVENT_IDS.ADDRESS_ENTRY_NEXT;
export const ADDRESS_ENTRY_GO_BACK_EVENT_ID = EVENT_IDS.ADDRESS_ENTRY_GO_BACK;
export const ADDRESS_ENTRY_EXIT_EVENT_ID = EVENT_IDS.ADDRESS_ENTRY_EXIT;
export const ADDRESS_VERIFICATION_GO_BACK_EVENT_ID = EVENT_IDS.ADDRESS_VERIFICATION_GO_BACK;
export const ADDRESS_VERIFICATION_EXIT_EVENT_ID = EVENT_IDS.ADDRESS_VERIFICATION_EXIT;
export const CREATE_LISTING_EVENT_ID = EVENT_IDS.CREATE_LISTING;

// https://dataportal.zillowgroup.net/event-registry/5998
export const emitStartOnboardingEvent = ({ user }: { user: ZrmClickstreamUser | null }) =>
  _nextTrackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.START_ONBOARDING,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'view',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'zrm_user_onboarding',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3553
export const emitProfileBuilderNextEvent = ({ user }: { user: ZrmClickstreamUser | null }) =>
  _nextTrackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.PROFILE_BUILDER_NEXT,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'profile_builder',
      trigger_source_nm: 'next',
    },
    semantic: {
      semantic_event_nm: 'zrm_user_onboarding',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3554
export const emitProfileBuilderSaveAndExitEvent = ({ user }: { user: ZrmClickstreamUser | null }) =>
  _nextTrackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.PROFILE_BUILDER_SAVE_AND_EXIT,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'profile_builder',
      trigger_source_nm: 'save_and_exit',
    },
    semantic: {
      semantic_event_nm: 'zrm_user_onboarding',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3555
export const emitAddressEntryNextEvent = ({ user }: { user: ZrmClickstreamUser | null }) =>
  _nextTrackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.ADDRESS_ENTRY_NEXT,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'address_entry',
      trigger_source_nm: 'next',
    },
    semantic: {
      semantic_event_nm: 'add_address',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3556
export const emitAddressEntryGoBackEvent = ({ user }: { user: ZrmClickstreamUser | null }) =>
  _nextTrackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.ADDRESS_ENTRY_GO_BACK,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'address_entry',
      trigger_source_nm: 'go_back',
    },
    semantic: {
      semantic_event_nm: 'zrm_user_onboarding',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3557
export const emitAddressEntryExitEvent = ({ user }: { user: ZrmClickstreamUser | null }) =>
  _nextTrackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.ADDRESS_ENTRY_EXIT,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'address_entry',
      trigger_source_nm: 'exit',
    },
    semantic: {
      semantic_event_nm: 'zrm_user_onboarding',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3558
export const emitAddressVerificationGoBackEvent = ({ user }: { user: ZrmClickstreamUser | null }) =>
  _nextTrackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.ADDRESS_VERIFICATION_GO_BACK,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'address_verification',
      trigger_source_nm: 'go_back',
    },
    semantic: {
      semantic_event_nm: 'edit_address',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3559
export const emitAddressVerificationExitEvent = ({ user }: { user: ZrmClickstreamUser | null }) =>
  _nextTrackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.ADDRESS_VERIFICATION_EXIT,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'address_verification',
      trigger_source_nm: 'exit',
    },
    semantic: {
      semantic_event_nm: 'zrm_user_onboarding',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3560
export const emitCreateListingEvent = ({
  user,
  listing,
}: {
  user: ZrmClickstreamUser | null;
  listing: ZrmClickstreamListing;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CREATE_LISTING,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_user_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'address_verification',
      trigger_source_nm: 'confirm',
    },
    semantic: {
      semantic_event_nm: 'create_listing',
      topic_tag_txt: ['zrm_growth'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });
